/**
 * F17.2.9 — Pianta riflessa del controsoffitto.
 *
 * Vista dal BASSO (controsoffitto visto da sotto). Mostra:
 *  - Cornice perimetrale stanza (rettangolo con muri/profilo perimetrale)
 *  - Griglia orditura PRIMARIA (linee orizzontali con passo primarySpacingMm)
 *  - Griglia orditura SECONDARIA (linee perpendicolari verticali con
 *    passo secondarySpacingMm, solo per orditura doppia)
 *  - Pendini come puntini agli incroci della griglia pendinatura
 *    (passo hangerSpacingPrimaryMm × hangerSpacingPerpendicularMm)
 *  - Quote stanza + interassi + numero pendini totali
 *
 * Convenzione orientamento (vista dal basso):
 *  - Orditura PRIMARIA = righe orizzontali (più lunga, portante)
 *  - Orditura SECONDARIA = colonne verticali (perpendicolare, secondaria)
 *  - Profilo perimetrale = cornice esterna 4 lati
 *
 * Room size di default: 4m × 5m (20 m²). Personalizzabile via prop `roomDimsM`.
 */
import React, { useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { Map } from 'lucide-react';
import {
  buildCeilingSectionModel,
  estimateHangerCountPerSqm,
} from '../section-view/ceilingModel';
import type { LayerV2 } from '../../types';
import type { CeilingDimensionalState } from '../../hooks/useCeilingConfiguratorState';
import { formatNumber } from '@/utils/format';

interface CeilingPlanViewProps {
  layers: LayerV2[];
  ceiling: CeilingDimensionalState;
  /** Dimensioni stanza visualizzate in metri (default 4×5m = 20 m²). */
  roomDimsM?: { widthM: number; lengthM: number };
}

const CeilingPlanView: React.FC<CeilingPlanViewProps> = ({
  layers,
  ceiling,
  roomDimsM = { widthM: 4, lengthM: 5 },
}) => {
  const model = useMemo(() => buildCeilingSectionModel(layers, ceiling), [layers, ceiling]);

  if (model.blocks.length <= 1) {
    return (
      <Card className="p-12 text-center text-sm text-muted-foreground border-dashed min-h-[420px] flex items-center justify-center">
        <div>
          <Map className="h-12 w-12 mx-auto mb-3 text-muted-foreground/40" />
          <p>Aggiungi materiali per vedere la pianta riflessa</p>
        </div>
      </Card>
    );
  }

  // ===== Layout SVG =====
  const VB_W = 1280;
  const VB_H = 720;
  const HEADER_H = 70;
  const FOOTER_H = 50;
  const PAD_LEFT = 80;
  const PAD_RIGHT = 80;
  const QUOTE_AREA_TOP = 50;
  const QUOTE_AREA_BOTTOM = 40;
  const SIDEBAR_W = 280;

  const drawAreaW = VB_W - PAD_LEFT - PAD_RIGHT - SIDEBAR_W;
  const drawAreaH = VB_H - HEADER_H - FOOTER_H - QUOTE_AREA_TOP - QUOTE_AREA_BOTTOM;

  // Conversioni
  const roomW_mm = roomDimsM.widthM * 1000;
  const roomL_mm = roomDimsM.lengthM * 1000;
  const areaSqm = roomDimsM.widthM * roomDimsM.lengthM;

  // Scale: fit stanza in (drawAreaW × drawAreaH) preservando aspect ratio
  const scaleX = drawAreaW / roomW_mm;
  const scaleY = drawAreaH / roomL_mm;
  const scale = Math.min(scaleX, scaleY);

  const roomPxW = roomW_mm * scale;
  const roomPxL = roomL_mm * scale;
  const roomX = PAD_LEFT + (drawAreaW - roomPxW) / 2;
  const roomY = HEADER_H + QUOTE_AREA_TOP + (drawAreaH - roomPxL) / 2;

  // === Calcolo posizioni griglie ===
  // Orditura primaria: linee ORIZZONTALI (lungo X), passo primarySpacingMm su Y
  const primaryLines: number[] = [];
  for (let yMm = ceiling.primarySpacingMm / 2; yMm < roomL_mm; yMm += ceiling.primarySpacingMm) {
    primaryLines.push(roomY + yMm * scale);
  }

  // Orditura secondaria: linee VERTICALI (lungo Y), passo secondarySpacingMm su X
  const secondaryLines: number[] = [];
  if (model.hasDoubleStructure && ceiling.secondarySpacingMm) {
    for (let xMm = ceiling.secondarySpacingMm / 2; xMm < roomW_mm; xMm += ceiling.secondarySpacingMm) {
      secondaryLines.push(roomX + xMm * scale);
    }
  }

  // Pendini: griglia di puntini su X×Y (solo se pendinato)
  type HangerPoint = { cx: number; cy: number };
  const hangerPoints: HangerPoint[] = [];
  if (model.isSuspended) {
    const spacingXMm = ceiling.hangerSpacingPerpendicularMm;
    const spacingYMm = ceiling.hangerSpacingPrimaryMm;
    for (let yMm = spacingYMm / 2; yMm < roomL_mm; yMm += spacingYMm) {
      for (let xMm = spacingXMm / 2; xMm < roomW_mm; xMm += spacingXMm) {
        hangerPoints.push({
          cx: roomX + xMm * scale,
          cy: roomY + yMm * scale,
        });
      }
    }
  }

  // === Conteggi ===
  const primaryProfilesMlTotal = ((roomL_mm / ceiling.primarySpacingMm) * roomW_mm) / 1000; // ml
  const secondaryProfilesMlTotal = model.hasDoubleStructure && ceiling.secondarySpacingMm
    ? ((roomW_mm / ceiling.secondarySpacingMm) * roomL_mm) / 1000
    : 0;
  const perimeterM = (roomDimsM.widthM + roomDimsM.lengthM) * 2;
  const hangerTotal = hangerPoints.length;
  const hangerCountPerSqm = model.isSuspended
    ? estimateHangerCountPerSqm(ceiling.hangerSpacingPrimaryMm, ceiling.hangerSpacingPerpendicularMm)
    : 0;

  // === SIDEBAR data ===
  const sidebarX = VB_W - SIDEBAR_W - PAD_RIGHT;

  return (
    <div className="bg-white border rounded-lg overflow-hidden shadow-sm" data-plan-view="true">
      <svg
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        className="w-full h-auto"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* === BACKGROUND === */}
        <rect x={0} y={0} width={VB_W} height={VB_H} fill="#ffffff" />

        {/* === HEADER === */}
        <g>
          <text x={PAD_LEFT - 20} y={32} fontSize={20} fontWeight={700} fill="#0F172A" fontFamily="Inter, system-ui, sans-serif">
            Pianta riflessa
          </text>
          <text x={PAD_LEFT - 20} y={52} fontSize={11} fill="#64748B" fontFamily="Inter, system-ui, sans-serif">
            Vista del controsoffitto dal basso · stanza di riferimento {roomDimsM.widthM}×{roomDimsM.lengthM}m ({formatNumber(areaSqm, 1)} m²)
          </text>
          <line x1={PAD_LEFT - 20} y1={HEADER_H} x2={VB_W - PAD_RIGHT} y2={HEADER_H} stroke="#E2E8F0" strokeWidth={0.5} />
        </g>

        {/* === STANZA: cornice + sfondo === */}
        <g>
          {/* Sfondo stanza (off-white) */}
          <rect x={roomX} y={roomY} width={roomPxW} height={roomPxL} fill="#FAFAFA" stroke="#E4E4E7" strokeWidth={0.5} />

          {/* Profilo perimetrale (cornice ~5mm rappresentata da 8px) */}
          <rect
            x={roomX}
            y={roomY}
            width={roomPxW}
            height={roomPxL}
            fill="none"
            stroke="#71717A"
            strokeWidth={6}
            opacity={0.35}
          />
          <rect
            x={roomX + 3}
            y={roomY + 3}
            width={roomPxW - 6}
            height={roomPxL - 6}
            fill="none"
            stroke="#3F3F46"
            strokeWidth={1.2}
          />
        </g>

        {/* === GRIGLIA SECONDARIA (sotto, perpendicolare) === */}
        {secondaryLines.length > 0 && (
          <g opacity={0.5}>
            {secondaryLines.map((x, i) => (
              <line
                key={`sec-${i}`}
                x1={x}
                y1={roomY + 4}
                x2={x}
                y2={roomY + roomPxL - 4}
                stroke="#94A3B8"
                strokeWidth={1.4}
                strokeDasharray="3 2"
              />
            ))}
          </g>
        )}

        {/* === GRIGLIA PRIMARIA (sopra, orizzontale) === */}
        <g>
          {primaryLines.map((y, i) => (
            <line
              key={`pri-${i}`}
              x1={roomX + 4}
              y1={y}
              x2={roomX + roomPxW - 4}
              y2={y}
              stroke="#475569"
              strokeWidth={2}
            />
          ))}
        </g>

        {/* === PENDINI (puntini) === */}
        {hangerPoints.length > 0 && (
          <g>
            {hangerPoints.map((p, i) => (
              <g key={`h-${i}`}>
                {/* Alone chiaro */}
                <circle cx={p.cx} cy={p.cy} r={5.5} fill="#1E40AF" opacity={0.15} />
                {/* Punto */}
                <circle cx={p.cx} cy={p.cy} r={2.8} fill="#1E40AF" stroke="#ffffff" strokeWidth={0.8} />
              </g>
            ))}
          </g>
        )}

        {/* === QUOTE STANZA === */}
        <g>
          {/* Larghezza (sopra) */}
          <DimensionLineH
            x1={roomX}
            x2={roomX + roomPxW}
            y={roomY - 25}
            label={`${formatNumber(roomDimsM.widthM, 2)} m`}
            strong
          />
          {/* Lunghezza (sinistra) */}
          <DimensionLineV
            x={roomX - 25}
            y1={roomY}
            y2={roomY + roomPxL}
            label={`${formatNumber(roomDimsM.lengthM, 2)} m`}
            strong
          />
        </g>

        {/* === SIDEBAR DX === */}
        <g>
          <rect x={sidebarX} y={HEADER_H + 12} width={SIDEBAR_W - 10} height={VB_H - HEADER_H - FOOTER_H - 24} fill="#FAFAFA" stroke="#E4E4E7" strokeWidth={0.5} rx={4} />
          <SidebarText
            x={sidebarX + 16}
            startY={HEADER_H + 36}
            rows={[
              { kind: 'title', label: 'STIMA PER LA STANZA' },
              { kind: 'gap' },
              { kind: 'row', label: 'Area', value: `${formatNumber(areaSqm, 2)} m²` },
              { kind: 'row', label: 'Perimetro', value: `${formatNumber(perimeterM, 1)} m` },
              { kind: 'gap' },
              { kind: 'title', label: 'ORDITURA' },
              { kind: 'gap' },
              { kind: 'row', label: 'Profili primaria', value: `${formatNumber(primaryProfilesMlTotal, 1)} ml` },
              ...(model.hasDoubleStructure
                ? [{ kind: 'row' as const, label: 'Profili secondaria', value: `${formatNumber(secondaryProfilesMlTotal, 1)} ml` }]
                : []),
              { kind: 'row', label: 'Profilo perimetrale', value: `${formatNumber(perimeterM, 1)} ml` },
              ...(model.isSuspended
                ? [
                  { kind: 'gap' as const },
                  { kind: 'title' as const, label: 'PENDINI' },
                  { kind: 'gap' as const },
                  { kind: 'row' as const, label: 'Densità/m²', value: formatNumber(hangerCountPerSqm, 2) },
                  { kind: 'row' as const, label: 'Tot. stanza', value: String(hangerTotal) },
                  { kind: 'row' as const, label: 'Passo', value: `${ceiling.hangerSpacingPrimaryMm}×${ceiling.hangerSpacingPerpendicularMm} mm` },
                ]
                : []),
              { kind: 'gap' },
              { kind: 'legend' },
            ]}
            isSuspended={model.isSuspended}
            hasDouble={model.hasDoubleStructure}
          />
        </g>

        {/* === FOOTER === */}
        <g>
          <line x1={PAD_LEFT - 20} y1={VB_H - FOOTER_H} x2={VB_W - PAD_RIGHT} y2={VB_H - FOOTER_H} stroke="#E2E8F0" strokeWidth={0.5} />
          <text x={PAD_LEFT - 20} y={VB_H - FOOTER_H + 18} fontSize={9} fill="#64748B" fontFamily="Inter, system-ui, sans-serif">
            Pianta riflessa indicativa per stanza rettangolare {roomDimsM.widthM}×{roomDimsM.lengthM}m · le quantità definitive si calcolano sul perimetro reale al &quot;Aggiungi a preventivo&quot;
          </text>
          <text x={PAD_LEFT - 20} y={VB_H - FOOTER_H + 34} fontSize={9} fill="#94A3B8" fontFamily="Inter, system-ui, sans-serif">
            Vista dal basso · DryConfig · scala approssimativa
          </text>
        </g>
      </svg>
    </div>
  );
};

// ============================================================================
// Helpers SVG
// ============================================================================

const DimensionLineH: React.FC<{ x1: number; x2: number; y: number; label: string; strong?: boolean }> = ({
  x1, x2, y, label, strong = false,
}) => {
  const len = Math.abs(x2 - x1);
  if (len < 10) return null;
  const tickH = 8;
  return (
    <g>
      <line x1={x1} y1={y} x2={x2} y2={y} stroke="#0F172A" strokeWidth={strong ? 0.8 : 0.4} />
      <line x1={x1} y1={y - tickH / 2} x2={x1} y2={y + tickH / 2} stroke="#0F172A" strokeWidth={strong ? 0.8 : 0.4} />
      <line x1={x2} y1={y - tickH / 2} x2={x2} y2={y + tickH / 2} stroke="#0F172A" strokeWidth={strong ? 0.8 : 0.4} />
      <text
        x={(x1 + x2) / 2}
        y={y - 6}
        fontSize={11}
        fontWeight={strong ? 700 : 400}
        fill="#0F172A"
        textAnchor="middle"
        fontFamily="Inter, system-ui, sans-serif"
      >
        {label}
      </text>
    </g>
  );
};

const DimensionLineV: React.FC<{ x: number; y1: number; y2: number; label: string; strong?: boolean }> = ({
  x, y1, y2, label, strong = false,
}) => {
  const len = Math.abs(y2 - y1);
  if (len < 10) return null;
  const tickW = 8;
  return (
    <g>
      <line x1={x} y1={y1} x2={x} y2={y2} stroke="#0F172A" strokeWidth={strong ? 0.8 : 0.4} />
      <line x1={x - tickW / 2} y1={y1} x2={x + tickW / 2} y2={y1} stroke="#0F172A" strokeWidth={strong ? 0.8 : 0.4} />
      <line x1={x - tickW / 2} y1={y2} x2={x + tickW / 2} y2={y2} stroke="#0F172A" strokeWidth={strong ? 0.8 : 0.4} />
      <text
        x={x - 6}
        y={(y1 + y2) / 2}
        fontSize={11}
        fontWeight={strong ? 700 : 400}
        fill="#0F172A"
        textAnchor="end"
        fontFamily="Inter, system-ui, sans-serif"
        transform={`rotate(-90 ${x - 6} ${(y1 + y2) / 2})`}
      >
        {label}
      </text>
    </g>
  );
};

type SidebarRow =
  | { kind: 'title'; label: string }
  | { kind: 'row'; label: string; value: string }
  | { kind: 'gap' }
  | { kind: 'legend' };

const SidebarText: React.FC<{
  x: number;
  startY: number;
  rows: SidebarRow[];
  isSuspended: boolean;
  hasDouble: boolean;
}> = ({ x, startY, rows, isSuspended, hasDouble }) => {
  const elements: React.ReactNode[] = [];
  let cursorY = startY;
  const innerW = 250;

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    if (row.kind === 'title') {
      elements.push(
        <text key={i} x={x} y={cursorY} fontSize={9} fontWeight={700} fill="#94A3B8" letterSpacing="0.1em" fontFamily="Inter, system-ui, sans-serif">
          {row.label}
        </text>,
      );
      cursorY += 14;
    } else if (row.kind === 'row') {
      elements.push(
        <g key={i}>
          <text x={x} y={cursorY} fontSize={11} fill="#64748B" fontFamily="Inter, system-ui, sans-serif">{row.label}</text>
          <text x={x + innerW} y={cursorY} fontSize={12} fontWeight={600} fill="#0F172A" textAnchor="end" fontFamily="ui-monospace, monospace">
            {row.value}
          </text>
        </g>,
      );
      cursorY += 18;
    } else if (row.kind === 'gap') {
      cursorY += 8;
    } else if (row.kind === 'legend') {
      // Legenda simboli
      elements.push(
        <g key={i}>
          <text x={x} y={cursorY} fontSize={9} fontWeight={700} fill="#94A3B8" letterSpacing="0.1em" fontFamily="Inter, system-ui, sans-serif">
            LEGENDA
          </text>
        </g>,
      );
      cursorY += 16;
      // Profilo primario
      elements.push(
        <g key={`${i}-prim`}>
          <line x1={x} y1={cursorY - 3} x2={x + 24} y2={cursorY - 3} stroke="#475569" strokeWidth={2} />
          <text x={x + 32} y={cursorY} fontSize={10} fill="#52525B" fontFamily="Inter, system-ui, sans-serif">
            Orditura primaria
          </text>
        </g>,
      );
      cursorY += 16;
      // Profilo secondario (solo se doppia)
      if (hasDouble) {
        elements.push(
          <g key={`${i}-sec`}>
            <line x1={x} y1={cursorY - 3} x2={x + 24} y2={cursorY - 3} stroke="#94A3B8" strokeWidth={1.4} strokeDasharray="3 2" opacity={0.5} />
            <text x={x + 32} y={cursorY} fontSize={10} fill="#52525B" fontFamily="Inter, system-ui, sans-serif">
              Orditura secondaria
            </text>
          </g>,
        );
        cursorY += 16;
      }
      // Pendino (solo se pendinato)
      if (isSuspended) {
        elements.push(
          <g key={`${i}-hang`}>
            <circle cx={x + 12} cy={cursorY - 3} r={5.5} fill="#1E40AF" opacity={0.15} />
            <circle cx={x + 12} cy={cursorY - 3} r={2.8} fill="#1E40AF" stroke="#ffffff" strokeWidth={0.8} />
            <text x={x + 32} y={cursorY} fontSize={10} fill="#52525B" fontFamily="Inter, system-ui, sans-serif">
              Pendino di sospensione
            </text>
          </g>,
        );
        cursorY += 16;
      }
      // Profilo perimetrale
      elements.push(
        <g key={`${i}-perim`}>
          <rect x={x} y={cursorY - 8} width={24} height={6} fill="none" stroke="#3F3F46" strokeWidth={1.2} />
          <text x={x + 32} y={cursorY} fontSize={10} fill="#52525B" fontFamily="Inter, system-ui, sans-serif">
            Profilo perimetrale
          </text>
        </g>,
      );
      cursorY += 16;
    }
  }

  return <g>{elements}</g>;
};

export default CeilingPlanView;
